import type { DealerOnboardInput, SessionUser } from '@motoveo/shared';
import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../api/client';
import { Layout } from '../components/Layout';

interface ContactDraft {
  name: string;
  phone: string;
  email: string;
  role: string;
  isPrimary: boolean;
  captureConsent: boolean;
}

const emptyContact = (isPrimary = false): ContactDraft => ({
  name: '',
  phone: '',
  email: '',
  role: '',
  isPrimary,
  captureConsent: true,
});

/** Onboard a dealer: the company + its contacts + (optionally) each contact's B2B consent, atomically. */
export function DealerFormPage({ user }: { user: SessionUser }) {
  const navigate = useNavigate();
  const [businessName, setBusinessName] = useState('');
  const [state, setState] = useState('');
  const [notes, setNotes] = useState('');
  // refined model — franchise-only launch: onboarding defaults to a franchise store (only
  // status='active' AND franchise=true dealers receive blasts).
  const [franchise, setFranchise] = useState(true);
  const [contacts, setContacts] = useState<ContactDraft[]>([emptyContact(true)]);

  const setContact = (i: number, patch: Partial<ContactDraft>) =>
    setContacts((cs) => cs.map((c, idx) => (idx === i ? { ...c, ...patch } : c)));

  const onboard = useMutation({
    mutationFn: () => {
      const payload: DealerOnboardInput = {
        business_name: businessName.trim(),
        state: state.trim().toUpperCase(),
        franchise,
        network_notes: notes.trim() || undefined,
        contacts: contacts.map((c) => ({
          name: c.name.trim() || undefined,
          phone_e164: c.phone.trim() || undefined,
          email: c.email.trim() || undefined,
          role_title: c.role.trim() || undefined,
          is_primary: c.isPrimary,
          consent: c.captureConsent && (c.phone.trim() || c.email.trim())
            ? { channel: c.email.trim() && !c.phone.trim() ? 'email' : 'sms', consent_type: 'implied_business', capture_source: 'dealer_onboarding' }
            : undefined,
        })),
      };
      return api.dealers.onboard(payload);
    },
    onSuccess: (res) => navigate(`/dealers/${res.dealer_id}`),
  });

  const canSubmit =
    businessName.trim().length > 0 &&
    state.trim().length === 2 &&
    contacts.every((c) => c.phone.trim() || c.email.trim());

  return (
    <Layout user={user}>
      <h2>Onboard dealer</h2>
      <form
        className="card form"
        onSubmit={(e) => {
          e.preventDefault();
          onboard.mutate();
        }}
      >
        <div className="form-grid">
          <label>
            Business name *
            <input value={businessName} onChange={(e) => setBusinessName(e.target.value)} required maxLength={200} />
          </label>
          <label>
            State *
            <input value={state} onChange={(e) => setState(e.target.value)} maxLength={2} placeholder="FL" required />
          </label>
        </div>
        <label>
          Network notes
          <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} />
        </label>
        <div className="checks">
          <label className="check">
            <input type="checkbox" checked={franchise} onChange={(e) => setFranchise(e.target.checked)} /> Official Porsche
            FRANCHISE store — required to receive blasts (the launch network is franchise-only)
          </label>
        </div>

        <h3>Contacts (consent + opt-out attach here, not the company)</h3>
        {contacts.map((c, i) => (
          <div key={i} className="card">
            <div className="row-between">
              <strong>Contact {i + 1}</strong>
              {contacts.length > 1 ? (
                <button type="button" className="plain" onClick={() => setContacts((cs) => cs.filter((_, idx) => idx !== i))}>
                  Remove
                </button>
              ) : null}
            </div>
            <div className="form-grid">
              <label>
                Name
                <input value={c.name} onChange={(e) => setContact(i, { name: e.target.value })} />
              </label>
              <label>
                Phone (E.164)
                <input value={c.phone} onChange={(e) => setContact(i, { phone: e.target.value })} placeholder="+13055551234" />
              </label>
              <label>
                Email
                <input type="email" value={c.email} onChange={(e) => setContact(i, { email: e.target.value })} />
              </label>
              <label>
                Role
                <input value={c.role} onChange={(e) => setContact(i, { role: e.target.value })} placeholder="Acquisitions" />
              </label>
            </div>
            <div className="checks">
              <label className="check">
                <input type="checkbox" checked={c.isPrimary} onChange={(e) => setContact(i, { isPrimary: e.target.checked })} /> Primary
              </label>
              <label className="check">
                <input type="checkbox" checked={c.captureConsent} onChange={(e) => setContact(i, { captureConsent: e.target.checked })} /> Capture
                B2B consent (implied business) — required to blast this contact
              </label>
            </div>
          </div>
        ))}
        <button type="button" className="plain" onClick={() => setContacts((cs) => [...cs, emptyContact()])}>
          + Add contact
        </button>

        <div className="row-between">
          <button type="submit" disabled={onboard.isPending || !canSubmit}>
            {onboard.isPending ? 'Onboarding…' : 'Onboard dealer'}
          </button>
          {onboard.isError ? <span className="error">{(onboard.error as Error).message}</span> : null}
        </div>
      </form>
    </Layout>
  );
}
